import { Box, Flex, Grid, Icon, List, Text } from "@chakra-ui/react";
import { ReadMore } from "@verivest/shared-components/src/components/common/ReadMore";
import { ManagerOverviewInfo } from "@verivest/shared-components/src/components/manager-profile/ManagerOverviewInfo";
import { OpportunityCover } from "@verivest/shared-components/src/components/manager-profile/OpportunityCover";
import useGetDynamicConfigIsManager from "@verivest/shared-components/src/dynamic-config/hooks/useGetDynamicConfigIsManager";
import React from "react";
import { Helmet } from "react-helmet";
import Sticky from "react-stickynode";
import { dateToString, htmlToText } from "../../../../../utils/utils";
import { PinIcon } from "../../../../general";
import ListItemDot from "../../../../general/List/ListItemDot";
import { VeriImageCarousel } from "../../../../general/VeriImageCarousel";
import VeriHeading from "../../../../VeriHeading";
import { SectionContainer } from "../../SectionContainer";
import { OpportunityDocuments } from "../OpportunityDocuments";
import { OpportunityRightSide } from "../OpportunityRightSide";
import { OpportunityViewProps } from "./OpportunityView.types";

const criterias = [
  {
    name: "Property Type",
    type: "property_type",
  },
  {
    name: "Strategy",
    type: "strategy",
  },
  {
    name: "Investment Type",
    type: "investment_type",
  },
  {
    name: "Risk Profile",
    type: "risk_profile",
  },
  {
    name: "Program Type",
    type: "investment_program",
  },
];

export const OpportunityView: React.FC<OpportunityViewProps> = ({
  opportunity,
  isPreview,
  manager: _manager,
  ...rest
}) => {
  const manager = _manager || opportunity.manager;
  const isManager = useGetDynamicConfigIsManager();

  if (!manager) return <></>;

  const description = opportunity.descriptionHtml || "";

  return (
    <Box {...rest}>
      <Helmet>
        <title>{`${opportunity.name} - ${manager.name}`}</title>
        <meta
          name="description"
          content={opportunity.metaDescription || htmlToText(description)}
        />
        {opportunity.images && opportunity.images.length > 0 && (
          <meta property="og:image" content={opportunity.images[0].url || ""} />
        )}
      </Helmet>
      <SectionContainer noBorder pt="24px">
        <OpportunityCover
          opportunity={opportunity}
          manager={manager}
          mb={6}
          wordBreak="break-word"
        />
      </SectionContainer>
      <SectionContainer noBorder>
        <Grid
          gap={"32px"}
          templateColumns={{ base: "minmax(0, 1fr)", md: "minmax(0, 2fr) 1fr" }}
          mt="8px"
        >
          <Box display={{ base: "block", md: "none" }}>
            <OpportunityRightSide manager={manager} opportunity={opportunity} />
          </Box>
          <Box>
            <SectionContainer noPadding bg="background">
              <VeriHeading textStyle="h4" className={"sm:hidden"}>
                Opportunity overview
              </VeriHeading>
              <Flex mt={"8px"}>
                {manager.address && manager.address.city && (
                  <Flex alignItems="center" mr="18px">
                    <Icon as={PinIcon} size={"16px"} mr="10px" />
                    <Text color="text.grey">{`${manager.address.city}, ${manager.address.region}`}</Text>
                  </Flex>
                )}
              </Flex>
              <Grid
                mt="32px"
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={8}
              >
                <ManagerOverviewInfo
                  title="Inception Date"
                  text={
                    opportunity.inceptionDate
                      ? dateToString(new Date(opportunity.inceptionDate))
                      : "No date provided"
                  }
                />
                {criterias.map((c) => {
                  const data = opportunity.attributeitems?.filter(
                    (a) => a.attributeType === c.type,
                  );
                  if (data && (data?.length || 0) > 0) {
                    return (
                      <ManagerOverviewInfo
                        key={c.type}
                        title={c.name}
                        text={data.map((d) => d.name).join(", ")}
                      />
                    );
                  }
                  return <></>;
                })}
              </Grid>
            </SectionContainer>
            <SectionContainer noPadding bg="background" mt="48px">
              <Box>
                <VeriHeading textStyle="h4" mb="24px">
                  Opportunity description
                </VeriHeading>
                <ReadMore limit={400} text={description} />
                <VeriImageCarousel
                  video={opportunity.marketplaceVideoUrl || ""}
                  mt={6}
                  images={opportunity.images}
                  opportunityId={String(opportunity.id)}
                />
              </Box>
            </SectionContainer>

            {opportunity.opportunitypoints &&
              opportunity.opportunitypoints.length > 0 && (
                <SectionContainer noPadding bg="background" mt="48px">
                  <Box>
                    <VeriHeading textStyle="h4" mb="24px">
                      Key deal points
                    </VeriHeading>

                    {opportunity.opportunitypoints &&
                      opportunity.opportunitypoints.length > 0 && (
                        <List mt="30px" spacing={4}>
                          {opportunity.opportunitypoints.map((p) => (
                            <ListItemDot key={p.id}>{p.keyPoint}</ListItemDot>
                          ))}
                        </List>
                      )}
                  </Box>
                </SectionContainer>
              )}

            <OpportunityDocuments
              opportunity={opportunity}
              manager={manager}
              mb={!isManager ? "48px" : undefined}
            />
          </Box>
          <Box display={{ base: "none", md: "block" }} id="right-side-s">
            <Sticky
              activeClass="right-side-active"
              top={20}
              bottomBoundary="#right-side-s"
            >
              <Box pb="48px">
                <OpportunityRightSide
                  isPreview={isPreview}
                  manager={manager}
                  opportunity={opportunity}
                />
              </Box>
            </Sticky>
          </Box>
        </Grid>
      </SectionContainer>
    </Box>
  );
};
